import { _callApi } from "../Services/baseService";
import * as Types from "../constants/ReportDashboard";
import {
  buildFilterValuesQuery,
  buildDashboardQuery,
  buildDashboardQueryParams,
  parseFilterPageResponse,
  parsePatientsVolumeSummaryResponse,
  parseProviderTimeSummaryResponse,
  parsePatientEngagementResponse,
  parseAdherenceTrendResponse,
  parseRtmClaimsResponse,
  canFetchReportDashboardData,
  formatReportDashboardDate,
} from "../utils/ReportDashboard";
import {
  parseRtmClaimDetailsPage,
  RTM_CLAIM_DETAILS_PAGE_SIZE,
  downloadRtmClaimDetailsCsv,
} from "../utils/rtmClaims";

const callFilterValuesApi = async (endpoint, queryParams) => {
  const qs = buildFilterValuesQuery(queryParams);
  return _callApi({}, "REACT_APP_CLINIC_ROLES", "", 2, `reports/filter-values/${endpoint}${qs}`);
};

const dispatchFilterFailure = (dispatch, type, message, append) => {
  dispatch({ type, payload: { message, append } });
};

export const fetchFilterCustomers = (therapistId, profiles, pageNo = 0, append = false) => async (dispatch) => {
  dispatch({ type: Types.FETCH_RD_FILTER_CUSTOMERS_REQUEST, payload: { append } });
  try {
    const response = await callFilterValuesApi("customers", { therapistId, profiles, pageNo });
    if (response && parseInt(response.status) === 200) {
      dispatch({
        type: Types.FETCH_RD_FILTER_CUSTOMERS_SUCCESS,
        payload: { ...parseFilterPageResponse(response), append },
      });
    } else {
      dispatchFilterFailure(
        dispatch,
        Types.FETCH_RD_FILTER_CUSTOMERS_FAILURE,
        response?.msg || "Failed to load customers",
        append
      );
    }
  } catch (error) {
    dispatchFilterFailure(dispatch, Types.FETCH_RD_FILTER_CUSTOMERS_FAILURE, error.message, append);
  }
};

export const fetchFilterLocations = (
  therapistId,
  profiles,
  parentClinicIds = [],
  pageNo = 0,
  append = false
) => async (dispatch) => {
  dispatch({ type: Types.FETCH_RD_FILTER_LOCATIONS_REQUEST, payload: { append } });
  try {
    const response = await callFilterValuesApi("locations", {
      therapistId,
      profiles,
      parentClinicIds,
      pageNo,
    });
    if (response && parseInt(response.status) === 200) {
      dispatch({
        type: Types.FETCH_RD_FILTER_LOCATIONS_SUCCESS,
        payload: { ...parseFilterPageResponse(response), append },
      });
    } else {
      dispatchFilterFailure(
        dispatch,
        Types.FETCH_RD_FILTER_LOCATIONS_FAILURE,
        response?.msg || "Failed to load locations",
        append
      );
    }
  } catch (error) {
    dispatchFilterFailure(dispatch, Types.FETCH_RD_FILTER_LOCATIONS_FAILURE, error.message, append);
  }
};

export const fetchFilterProviders = (
  therapistId,
  profiles,
  parentClinicIds = [],
  childClinicIds = [],
  pageNo = 0,
  append = false
) => async (dispatch) => {
  dispatch({ type: Types.FETCH_RD_FILTER_PROVIDERS_REQUEST, payload: { append } });
  try {
    const response = await callFilterValuesApi("providers", {
      therapistId,
      profiles,
      parentClinicIds,
      childClinicIds,
      pageNo,
    });
    if (response && parseInt(response.status) === 200) {
      dispatch({
        type: Types.FETCH_RD_FILTER_PROVIDERS_SUCCESS,
        payload: { ...parseFilterPageResponse(response), append },
      });
    } else {
      dispatchFilterFailure(
        dispatch,
        Types.FETCH_RD_FILTER_PROVIDERS_FAILURE,
        response?.msg || "Failed to load providers",
        append
      );
    }
  } catch (error) {
    dispatchFilterFailure(dispatch, Types.FETCH_RD_FILTER_PROVIDERS_FAILURE, error.message, append);
  }
};

export const fetchReportDashboard = () => async (dispatch, getState) => {
  if (!canFetchReportDashboardData(getState())) return;

  const queryParams = buildDashboardQueryParams(getState());

  dispatch({ type: Types.FETCH_REPORT_DASHBOARD_REQUEST });
  try {
    const qs = buildDashboardQuery(queryParams);
    const response = await _callApi(
      {},
      "REACT_APP_CLINIC_ROLES",
      "",
      2,
      `reports/dashboard/patients-volume-summary${qs}`
    );
    if (response && parseInt(response.status) === 200) {
      dispatch({
        type: Types.FETCH_REPORT_DASHBOARD_SUCCESS,
        payload: parsePatientsVolumeSummaryResponse(response),
      });
    } else {
      dispatch({
        type: Types.FETCH_REPORT_DASHBOARD_FAILURE,
        payload: response?.msg || "Failed to load dashboard",
      });
    }
  } catch (error) {
    dispatch({ type: Types.FETCH_REPORT_DASHBOARD_FAILURE, payload: error.message });
  }
};

export const fetchProviderTimeSummary = () => async (dispatch, getState) => {
  if (!canFetchReportDashboardData(getState())) return;

  const queryParams = buildDashboardQueryParams(getState());

  dispatch({ type: Types.FETCH_PROVIDER_TIME_SUMMARY_REQUEST });
  try {
    const qs = buildDashboardQuery(queryParams);
    const response = await _callApi(
      {},
      "REACT_APP_CLINIC_ROLES",
      "",
      2,
      `reports/dashboard/provider-time-summary${qs}`
    );
    if (response && parseInt(response.status) === 200) {
      dispatch({
        type: Types.FETCH_PROVIDER_TIME_SUMMARY_SUCCESS,
        payload: parseProviderTimeSummaryResponse(response),
      });
    } else {
      dispatch({
        type: Types.FETCH_PROVIDER_TIME_SUMMARY_FAILURE,
        payload: response?.msg || "Failed to load provider time summary",
      });
    }
  } catch (error) {
    dispatch({ type: Types.FETCH_PROVIDER_TIME_SUMMARY_FAILURE, payload: error.message });
  }
};

export const fetchAllKpis = (options = {}) => (dispatch, getState) => {
  const { force = false } = options;
  const { patientEngagement } = getState().reportDashboard;

  // Temporarily disabled — re-enable when total RTM claims KPI API is ready
  // if (force || !totalRtmClaims.data) {
  //   dispatch(fetchReportDashboard());
  // }
  if (force || !patientEngagement.data) {
    dispatch(fetchPatientEngagement());
  }
};

export const fetchPatientEngagement = () => async (dispatch, getState) => {
  if (!canFetchReportDashboardData(getState())) return;

  const queryParams = buildDashboardQueryParams(getState());

  dispatch({ type: Types.FETCH_PATIENT_ENGAGEMENT_REQUEST });
  try {
    const qs = buildDashboardQuery({ ...queryParams, includeAdherenceParams: true });
    const response = await _callApi(
      {},
      "REACT_APP_CLINIC_ROLES",
      "",
      2,
      `reports/patient-engagement/adherence${qs}`
    );
    if (response && parseInt(response.status) === 200) {
      dispatch({
        type: Types.FETCH_PATIENT_ENGAGEMENT_SUCCESS,
        payload: parsePatientEngagementResponse(response),
      });
    } else {
      dispatch({
        type: Types.FETCH_PATIENT_ENGAGEMENT_FAILURE,
        payload: response?.msg || "Failed to load patient engagement",
      });
    }
  } catch (error) {
    dispatch({ type: Types.FETCH_PATIENT_ENGAGEMENT_FAILURE, payload: error.message });
  }
};

export const fetchAdherenceTrend = () => async (dispatch, getState) => {
  if (!canFetchReportDashboardData(getState())) return;

  const queryParams = buildDashboardQueryParams(getState());

  dispatch({ type: Types.FETCH_ADHERENCE_TREND_REQUEST });
  try {
    const qs = buildDashboardQuery(queryParams);
    const response = await _callApi(
      {},
      "REACT_APP_CLINIC_ROLES",
      "",
      2,
      `reports/patient-engagement/adherence/trend${qs}`
    );
    if (response && parseInt(response.status) === 200) {
      dispatch({
        type: Types.FETCH_ADHERENCE_TREND_SUCCESS,
        payload: parseAdherenceTrendResponse(response),
      });
    } else {
      dispatch({
        type: Types.FETCH_ADHERENCE_TREND_FAILURE,
        payload: response?.msg || "Failed to load adherence trend",
      });
    }
  } catch (error) {
    dispatch({ type: Types.FETCH_ADHERENCE_TREND_FAILURE, payload: error.message });
  }
};

export const fetchRtmClaims = () => async (dispatch, getState) => {
  if (!canFetchReportDashboardData(getState())) return;

  const queryParams = buildDashboardQueryParams(getState());

  dispatch({ type: Types.FETCH_RTM_CLAIMS_REQUEST });
  try {
    const qs = buildDashboardQuery(queryParams);
    const response = await _callApi(
      {},
      "REACT_APP_CLINIC_ROLES",
      "",
      2,
      `reports/rtm-claims/cpt-breakdown${qs}`
    );
    if (response && parseInt(response.status) === 200) {
      dispatch({
        type: Types.FETCH_RTM_CLAIMS_SUCCESS,
        payload: parseRtmClaimsResponse(response),
      });
    } else {
      dispatch({
        type: Types.FETCH_RTM_CLAIMS_FAILURE,
        payload: response?.msg || "Failed to load RTM claims",
      });
    }
  } catch (error) {
    dispatch({ type: Types.FETCH_RTM_CLAIMS_FAILURE, payload: error.message });
  }
};

export const fetchRtmClaimDetails = (eligibleOnly = true, pageNo = 0, append = false) => async (
  dispatch,
  getState
) => {
  if (!canFetchReportDashboardData(getState())) return;

  const queryParams = buildDashboardQueryParams(getState());

  dispatch({
    type: Types.FETCH_RTM_CLAIM_DETAILS_REQUEST,
    payload: { eligibleOnly, append },
  });
  try {
    const qs = buildDashboardQuery({
      ...queryParams,
      pageNo,
      pageSize: RTM_CLAIM_DETAILS_PAGE_SIZE,
      eligibleOnly,
      downloadable: false,
    });
    const response = await _callApi(
      {},
      "REACT_APP_CLINIC_ROLES",
      "",
      2,
      `reports/rtm-claims/claim-details${qs}`
    );
    if (response && parseInt(response.status) === 200) {
      dispatch({
        type: Types.FETCH_RTM_CLAIM_DETAILS_SUCCESS,
        payload: { ...parseRtmClaimDetailsPage(response), append, eligibleOnly },
      });
    } else {
      dispatch({
        type: Types.FETCH_RTM_CLAIM_DETAILS_FAILURE,
        payload: {
          message: response?.msg || "Failed to load RTM claim details",
          append,
        },
      });
    }
  } catch (error) {
    dispatch({
      type: Types.FETCH_RTM_CLAIM_DETAILS_FAILURE,
      payload: { message: error.message, append },
    });
  }
};

export const setRtmClaimDetailsEligibleOnly = (eligibleOnly) => (dispatch) => {
  dispatch(fetchRtmClaimDetails(eligibleOnly, 0, false));
};

export const exportRtmClaimDetails = (eligibleOnly) => async (dispatch, getState) => {
  if (!canFetchReportDashboardData(getState())) {
    return { ok: false, error: "Filters are incomplete" };
  }

  const queryParams = buildDashboardQueryParams(getState());

  try {
    const qs = buildDashboardQuery({
      ...queryParams,
      pageNo: 0,
      pageSize: RTM_CLAIM_DETAILS_PAGE_SIZE,
      eligibleOnly,
      downloadable: true,
    });
    const response = await _callApi(
      {},
      "REACT_APP_CLINIC_ROLES",
      "",
      2,
      `reports/rtm-claims/claim-details${qs}`
    );
    if (response && parseInt(response.status) === 200) {
      const { list } = parseRtmClaimDetailsPage(response);
      downloadRtmClaimDetailsCsv(list, eligibleOnly, formatReportDashboardDate);
      return { ok: true };
    }
    return {
      ok: false,
      error: response?.msg || "Failed to export RTM claim details",
    };
  } catch (error) {
    return { ok: false, error: error.message };
  }
};

export const fetchSectionData = (options = {}) => (dispatch, getState) => {
  if (!canFetchReportDashboardData(getState())) return;

  const { force = false, filtersChanged = false } = options;
  const { activeSection } = getState().reportDashboard;

  if (activeSection === "patient-engagement") {
    if (force || !getState().reportDashboard.patientEngagement.data) {
      dispatch(fetchPatientEngagement());
    }
    if (force || !getState().reportDashboard.adherenceTrend.data) {
      dispatch(fetchAdherenceTrend());
    }
    return;
  }

  // Temporarily disabled — re-enable when RTM claims section is ready
  // if (activeSection === "rtm-claims") {
  //   dispatch(fetchRtmClaims());
  //   const { eligibleOnly } = getState().reportDashboard.rtmClaimDetails;
  //   dispatch(fetchRtmClaimDetails(eligibleOnly, 0, false));
  //   return;
  // }

  // Temporarily disabled — re-enable when provider time KPI API is ready
  // if (filtersChanged || !providerTime.data) {
  //   dispatch(fetchProviderTimeSummary());
  // }
  dispatch(fetchAllKpis(options));
};

export const setPatientEngagementThresholds = (thresholds) => (dispatch) => {
  dispatch({ type: Types.SET_PATIENT_ENGAGEMENT_THRESHOLDS, payload: thresholds });
  dispatch(fetchPatientEngagement());
};

export const resetReportDashboard = (dataOwnerTherapistId = null) => ({
  type: Types.RESET_REPORT_DASHBOARD,
  payload: { dataOwnerTherapistId },
});

/** Reset to dashboard overview when entering Reports from the gear menu or route mount */
export const enterReportDashboard = () => (dispatch, getState) => {
  const { activeSection, filtersSnapshotBeforeRtm, dataOwnerTherapistId } = getState().reportDashboard;
  const therapistId = getState().user?.profile?.id;
  let filtersChanged = false;

  if (therapistId && dataOwnerTherapistId !== therapistId) {
    dispatch(resetReportDashboard(therapistId));
    filtersChanged = true;
  }

  if (activeSection === "rtm-claims" && filtersSnapshotBeforeRtm) {
    dispatch({ type: Types.RESTORE_FILTERS_FROM_RTM_SNAPSHOT });
    filtersChanged = true;
  }

  if (activeSection !== "dashboard") {
    dispatch({ type: Types.SET_REPORT_DASHBOARD_ACTIVE_SECTION, payload: "dashboard" });
  }

  dispatch(fetchSectionData({
    force: filtersChanged || activeSection !== "dashboard",
    filtersChanged,
  }));
};

export const setActiveSection = (section) => (dispatch, getState) => {
  const { activeSection, selectedFilters, filtersSnapshotBeforeRtm } = getState().reportDashboard;
  let filtersChanged = false;

  if (activeSection === "rtm-claims" && section !== "rtm-claims") {
    if (filtersSnapshotBeforeRtm) {
      dispatch({ type: Types.RESTORE_FILTERS_FROM_RTM_SNAPSHOT });
      filtersChanged = true;
    }
  }

  if (section === "rtm-claims" && activeSection !== "rtm-claims") {
    const { timeframe, startDate, endDate, month } = selectedFilters;
    dispatch({
      type: Types.SNAPSHOT_FILTERS_BEFORE_RTM,
      payload: { timeframe, startDate, endDate, month },
    });

    if (timeframe === "CURRENT") {
      dispatch({
        type: Types.SET_REPORT_DASHBOARD_FILTER,
        payload: { key: "timeframe", value: "OVERALL" },
      });
      filtersChanged = true;
    }
  }

  dispatch({ type: Types.SET_REPORT_DASHBOARD_ACTIVE_SECTION, payload: section });
  dispatch(fetchSectionData({ force: filtersChanged, filtersChanged }));
};

export const setReportDashboardFilter = (key, value) => (dispatch) => {
  dispatch({ type: Types.SET_REPORT_DASHBOARD_FILTER, payload: { key, value } });
  dispatch(fetchSectionData({ force: true, filtersChanged: true }));
};

export const resetReportDashboardFilters = () => (dispatch) => {
  dispatch({ type: Types.RESET_REPORT_DASHBOARD_FILTERS });
  dispatch(fetchSectionData({ force: true, filtersChanged: true }));
};
